<template>
  <!-- The whole setup behind « Configuration »: every check as a NUMBERED card,
       in the setup order the server serves (each one only needs what comes
       before it), done or not, IN PLACE — progress fills in from the top, and
       the first amber card is plainly the next one. No « next step » marker on
       purpose: a check waiting on someone else (the accountant's TVQ number)
       would hold it for a week while the shop gets on with the rest.
       A failing card says the gap in amber and names its records; a done card
       carries a teal check — done reads by colour and mark, never by greying
       out. Every card is a plain link to the page that sets it (a failing one
       opens the FIRST record it names). No prose, no footer — closing is ✕,
       Échap or a click outside. Opened by a click only; fade + zoom, never a slide. -->
  <Dialog :open="open" @update:open="value => !value && emit('close')">
    <DialogContent class="flex max-h-[90vh] flex-col gap-0 p-0 sm:max-w-2xl" data-testid="setup-check-dialog">
      <DialogHeader class="border-b px-6 py-4">
        <DialogTitle class="flex items-center gap-2">
          {{ t('setupCheck.title') }}
          <span
            v-if="failing"
            class="inline-grid h-5 min-w-5 place-items-center rounded-full bg-tone-amber-soft px-1.5 text-xs font-bold tabular-nums text-tone-amber"
          >{{ failing }}</span>
        </DialogTitle>
        <DialogDescription data-testid="setup-check-progress">
          {{ t('setupCheck.progress', { ok: checks.length - failing, total: checks.length }) }}
        </DialogDescription>
      </DialogHeader>

      <!-- ONE column that never outgrows the dialog: a bare grid column sizes to
           its longest line (the names row), which scrolled the list sideways. -->
      <ol class="grid min-h-0 flex-1 grid-cols-[minmax(0,1fr)] gap-2 overflow-y-auto overflow-x-hidden px-6 py-5">
        <li v-for="(check, index) in checks" :key="check.key" class="min-w-0">
          <RouterLink
            :to="setupCheckTarget(check)"
            class="flex min-w-0 items-start gap-3 rounded-lg border bg-card p-3 hover:bg-muted focus-visible:bg-muted focus-visible:outline-none"
            :class="check.ok ? '' : 'border-tone-amber'"
            :data-testid="`setup-check-card-${check.key}`"
            :data-ok="check.ok"
            @click="emit('close')"
          >
            <span
              class="mt-0.5 grid h-5 w-5 shrink-0 place-items-center rounded-full"
              :class="check.ok ? 'bg-tone-teal text-white' : 'bg-tone-amber-soft'"
            >
              <Check v-if="check.ok" class="h-3.5 w-3.5" />
              <span v-else class="h-2 w-2 rounded-full bg-tone-amber" />
            </span>
            <span class="min-w-0 flex-1">
              <span class="block font-semibold">
                <span class="mr-1.5 tabular-nums text-muted-foreground" data-testid="setup-check-number">{{ index + 1 }}.</span>{{ t(`setupCheck.titles.${check.key}`) }}
              </span>
              <span v-if="check.ok" class="block text-sm text-tone-teal">{{ t('setupCheck.ok') }}</span>
              <template v-else>
                <span class="block text-sm font-medium text-tone-amber">{{ t(`setupCheck.lines.${check.key}`) }}</span>
                <span v-if="check.items.length" class="block text-sm break-words text-muted-foreground">{{ itemsSummary(check) }}</span>
              </template>
            </span>
            <ChevronRight class="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
          </RouterLink>
        </li>
      </ol>
    </DialogContent>
  </Dialog>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { Check, ChevronRight } from 'lucide-vue-next'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { failingChecks, itemsSummary, setupCheckTarget, type SetupCheck } from '@/lib/setupCheck'

const props = defineProps<{ open: boolean; checks: SetupCheck[] }>()
const emit = defineEmits<{ close: [] }>()
const { t } = useI18n()

const failing = computed(() => failingChecks(props.checks).length)
</script>
